<script setup lang="ts">
interface Props {
  page: number;
  totalPages: number;
  alwaysShow?: boolean;
}

const { page, totalPages, alwaysShow = false } = defineProps<Props>();

const emit = defineEmits<{
  'update:page': [page: number];
}>();

function goToPage(p: number) {
  if (p >= 1 && p <= totalPages) {
    emit('update:page', p);
  }
}

const visiblePages = computed(() => {
  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [1, 2];

  if (page > 3) pages.push('...');
  if (page > 2 && page < totalPages) pages.push(page);
  if (page < totalPages - 1) pages.push('...');

  pages.push(totalPages);

  return [...new Set(pages)];
});
</script>

<template>
  <div
    v-if="totalPages > 1 || alwaysShow"
    class="flex items-center justify-center gap-1.5 border-t border-[#1e3a8a]/30 px-2 pt-2.5 pb-[10px]"
  >
    <button
      class="flex h-7 w-7 items-center justify-center rounded-md text-xs !text-[#ffffff] hover:bg-[#1e40af]/40 disabled:opacity-25 transition-colors"
      :disabled="page <= 1"
      @click="goToPage(page - 1)"
    >
      &lt;
    </button>
    <template
      v-for="p in visiblePages"
      :key="p"
    >
      <span
        v-if="p === '...'"
        class="flex h-7 w-7 items-center justify-center text-xs !text-[#ffffff]/40"
      >
        ...
      </span>
      <button
        v-else
        class="flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold transition-all duration-150"
        :class="[
          p === page
            ? 'bg-gradient-to-b from-[#3b82f6] to-[#1d4ed8] !text-[#ffffff] shadow-md shadow-[#3b82f6]/30'
            : '!text-[#ffffff]/70 hover:bg-[#1e40af]/40 hover:!text-[#ffffff]',
        ]"
        @click="goToPage(p as number)"
      >
        {{ p }}
      </button>
    </template>
    <button
      class="flex h-7 w-7 items-center justify-center rounded-md text-xs !text-[#ffffff] hover:bg-[#1e40af]/40 disabled:opacity-25 transition-colors"
      :disabled="page >= totalPages"
      @click="goToPage(page + 1)"
    >
      &gt;
    </button>
  </div>
</template>
